export interface QuizQuestion {
  id: string;
  question: string;
  microcopy?: string;
  type: 'single' | 'multi' | 'text';
  options?: string[];
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: 'goal',
    question: 'What result do you most want from your content in the next 90 days?',
    microcopy: 'Pick the one that matters most right now.',
    type: 'single',
    options: ['More leads', 'Better visibility', 'More trust and authority', 'More booked calls', 'A consistent system'],
  },
  {
    id: 'posting_frequency',
    question: 'How many times per week are you currently posting?',
    microcopy: 'Be honest — no judgment here.',
    type: 'single',
    options: ['0', '1–2', '3–4', '5+'],
  },
  {
    id: 'organic_leads',
    question: 'Do you currently get leads directly from your organic content?',
    microcopy: 'Think about the last 30 days.',
    type: 'single',
    options: ['Yes, consistently', 'Occasionally', 'Never'],
  },
  {
    id: 'bottleneck',
    question: 'What is your biggest bottleneck right now?',
    microcopy: 'This helps us identify your #1 growth gap.',
    type: 'single',
    options: ['Lack of visibility', 'Not generating enough leads', 'Content not building trust', 'Inconsistent strategy', 'Lack of time/resources', "Don't understand content yet"],
  },
  {
    id: 'content_types',
    question: 'What types of content are you creating most?',
    microcopy: 'Select all that apply.',
    type: 'multi',
    options: ['Educational', 'Opinions', 'Personal brand', 'Case studies', 'Promotional'],
  },
  {
    id: 'clear_cta',
    question: 'Do you have a clear next step after someone consumes your content?',
    microcopy: 'A CTA, link, or offer they can act on.',
    type: 'single',
    options: ['Yes', 'Sometimes', 'No'],
  },
  {
    id: 'responsibility',
    question: 'Who is responsible for creating your content?',
    microcopy: 'Who does the heavy lifting?',
    type: 'single',
    options: ['Founder', 'Internal team', 'Agency', 'No one consistently'],
  },
  {
    id: 'service_area',
    question: 'Where do you primarily serve?',
    microcopy: 'This helps us tailor your roadmap.',
    type: 'single',
    options: ['Citywide', 'Statewide', 'Nationwide', 'International'],
  },
  {
    id: 'business_description',
    question: 'What does your business do and who do you help?',
    microcopy: 'A sentence or two is perfect.',
    type: 'text',
  },
  {
    id: 'monthly_revenue',
    question: 'What is your current monthly revenue?',
    microcopy: 'This stays completely confidential.',
    type: 'single',
    options: ['$0–20k', '$20k–50k', '$50k–100k', '$100k+'],
  },
];

export interface CategoryScores {
  visibility: number;
  consistency: number;
  authority: number;
  leadGeneration: number;
  conversion: number;
}

export function calculateScores(answers: Record<string, string | string[]>): { total: number; categories: CategoryScores } {
  const categories: CategoryScores = {
    visibility: 10,
    consistency: 10,
    authority: 10,
    leadGeneration: 10,
    conversion: 10,
  };

  // Consistency — posting frequency
  const freq = answers.posting_frequency as string;
  if (freq === '0') categories.consistency = 2;
  else if (freq === '1–2') categories.consistency = 8;
  else if (freq === '3–4') categories.consistency = 15;
  else if (freq === '5+') categories.consistency = 20;

  // Lead Generation — organic leads
  const leads = answers.organic_leads as string;
  if (leads === 'Yes, consistently') categories.leadGeneration = 20;
  else if (leads === 'Occasionally') categories.leadGeneration = 10;
  else if (leads === 'Never') categories.leadGeneration = 3;

  // Conversion — clear CTA
  const cta = answers.clear_cta as string;
  if (cta === 'Yes') categories.conversion = 20;
  else if (cta === 'Sometimes') categories.conversion = 10;
  else if (cta === 'No') categories.conversion = 2;

  // Responsibility affects consistency
  const resp = answers.responsibility as string;
  if (resp === 'Agency') categories.consistency = Math.min(20, categories.consistency + 5);
  else if (resp === 'Internal team') categories.consistency = Math.min(20, categories.consistency + 3);
  else if (resp === 'No one consistently') categories.consistency = Math.max(0, categories.consistency - 3);

  // Authority — content types
  const types = (answers.content_types as string[]) || [];
  const hasEdu = types.includes('Educational');
  const hasCase = types.includes('Case studies');
  const hasPromo = types.includes('Promotional');
  if (hasEdu && hasCase) categories.authority = 20;
  else if (types.length === 1 && hasPromo) categories.authority = 5;
  else if (types.length > 0) categories.authority = 12;
  else categories.authority = 3;

  // Visibility — based on goal + service area + posting
  const area = answers.service_area as string;
  if (area === 'International' || area === 'Nationwide') categories.visibility = 12;
  else if (area === 'Statewide') categories.visibility = 14;
  else categories.visibility = 16;
  if (freq === '5+') categories.visibility = Math.min(20, categories.visibility + 4);
  else if (freq === '0') categories.visibility = Math.max(0, categories.visibility - 6);

  // Bottleneck penalties
  const bottleneck = answers.bottleneck as string;
  if (bottleneck === "Don't understand content yet") {
    const penalty = 10;
    // spread penalty
    categories.authority = Math.max(0, categories.authority - 4);
    categories.consistency = Math.max(0, categories.consistency - 3);
    categories.visibility = Math.max(0, categories.visibility - 3);
  } else if (bottleneck === 'Inconsistent strategy') {
    categories.consistency = Math.max(0, categories.consistency - 3);
    categories.conversion = Math.max(0, categories.conversion - 2);
  }

  let total = categories.visibility + categories.consistency + categories.authority + categories.leadGeneration + categories.conversion;
  total = Math.max(0, Math.min(100, total));

  return { total, categories };
}

export function getDiagnosis(score: number): string {
  if (score < 50) return "You don't have a structured content system yet. Your growth is being limited by inconsistency and lack of direction.";
  if (score <= 75) return "You have some momentum, but key gaps in your strategy are holding back consistent lead generation.";
  return "You have a strong foundation. With optimization, you can scale faster and convert more efficiently.";
}

export function getWeakCategories(categories: CategoryScores): string[] {
  const entries = Object.entries(categories) as [string, number][];
  entries.sort((a, b) => a[1] - b[1]);
  const labels: Record<string, string> = {
    visibility: 'Improve visibility and reach',
    consistency: 'Build a consistent posting system',
    authority: 'Increase authority-driven content',
    leadGeneration: 'Strengthen lead generation from content',
    conversion: 'Build stronger conversion paths',
  };
  return entries.slice(0, 3).map(([key]) => labels[key]);
}
